<template>

    <div>
        <button
            type="button"
            class="p-2 my-3 bg-dark-blue items-center flex text-white font-nexa text-sm rounded"
            @click="open()"
        >
            Resend
        </button>

        <!-- Modal -->
        <teleport to="body">
            <div
                v-if="is_open"
                class="fixed inset-0 z-50 flex items-center justify-center"
                aria-live="assertive"
            >
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/40" @click="onBackdropClick" />

                <!-- Dialog -->
                <div
                    role="dialog"
                    aria-modal="true"
                    :aria-label="ariaLabel"
                    class="relative z-10 w-[22rem] max-w-[90vw] rounded-xl bg-white p-6 shadow-xl"
                >
                    <h2 class="mb-3 text-base font-semibold text-gray-800 font-nexa">
                        {{ title }}
                    </h2>

                    <div class="flex flex-col text-center">
                        <p class="mb-2 font-nexa text-gray-700">
                            {{ message }}
                        </p>

                        <!-- Custom fetched text -->
                        <div class="min-h-[2.5rem] mb-4">
                            <p v-if="fetch_error" class="text-sm text-red-600">
                                {{ fetch_error }}
                            </p>
                            <p v-else-if="is_fetching_text" class="text-sm text-gray-400 italic">
                                Please wait...
                            </p>
                            <div v-else class="text-sm text-gray-600">
                                <p class="mb-2">
                                    This {{ resend_content_type }} will be sent to:
                                </p>
                                <ul class="space-y-1">
                                    <li
                                        v-for="row in frequency_rows"
                                        :key="row.frequency"
                                    >
                                        {{ row.label }}:
                                        <strong class="font-bold underline">{{ row.count }}</strong>
                                    </li>
                                </ul>
                                <p class="mt-2 pt-2 border-t border-gray-200">
                                    Total:
                                    <strong class="font-bold underline">{{ resend_total }}</strong>
                                    subscribers
                                </p>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex justify-around">
                            <button
                                type="button"
                                class="p-2 my-2 flex items-center justify-center shadow-sm cursor-pointer text-white font-nexa text-sm rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                :class="{'bg-gray-400 cursor-not-allowed': is_confirming, 'bg-dark-blue hover:opacity-90': !is_confirming}"
                                :disabled="is_confirming"
                                @click="onConfirm"
                            >
                                <span v-if="is_confirming" class="loader mr-2"></span>
                                <span>{{ is_confirming ? confirmingLabel : confirmLabel }}</span>
                            </button>

                            <button
                                type="button"
                                class="p-2 my-2 bg-gray-300 text-gray-800 flex items-center justify-center shadow-sm cursor-pointer font-nexa text-sm rounded hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                @click="close"
                            >
                                {{ cancelLabel }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
export default defineComponent({
    props: {

        model_value: { type: Boolean, default: false },


        fetch_url: {
            type: String, default: '/resend-text'
        },
        resend_url: {
            type: String, default: '/resend'
        },


        title: { type: String, default: 'Confirm resend' },
        message: { type: String, default: 'Are you sure you want to resend this content?' },
        ariaLabel: { type: String, default: 'Confirm resend' },


        confirmLabel: { type: String, default: 'Yes, resend' },
        confirmingLabel: { type: String, default: 'Resending...' },
        cancelLabel: { type: String, default: 'Cancel' },


        content_type_id: { type: Number, default: 1 },
        content_id: { type: Number, default: 428 },


        closeOnBackdrop: { type: Boolean, default: true },
    },

    data() {
        return {
            is_open: this.model_value,
            selected_payload: null,
            resend_content_type: null,
            resend_amounts: {
                instant: 0,
                daily: 0,
                weekly: 0,
            },
            is_fetching_text: false,
            fetch_error: null,
            is_confirming: false,
        }
    },

    computed: {
        frequency_rows() {
            return [
                { frequency: 'instant', label: 'Instant', count: this.resend_amounts.instant },
                { frequency: 'daily', label: 'Daily', count: this.resend_amounts.daily },
                { frequency: 'weekly', label: 'Weekly', count: this.resend_amounts.weekly },
            ];
        },

        resend_total() {
            return this.frequency_rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
        },
    },

    methods: {
        open() {
            this.is_open = true
            console.log('Opened');
            console.log(this.fetch_url);
            this.fetch_text();
        },

        /** Close modal */
        close() {
            this.is_open = false;
        },

        onBackdropClick() {
            if (this.closeOnBackdrop) this.close()
        },

        onKeydown(e) {
            if (e.key === 'Escape') this.close()
        },

        async onConfirm() {
            if (this.is_confirming) return
            this.is_confirming = true
            try {
                // Emit event so parent can perform the resend
                fetch( `${this.resend_url}?content_type_id=${this.content_type_id}&content_id=${this.content_id}`,
                    { credentials: 'same-origin' } )

                    .then(res => res.json())
                    .then(data => {
                        if (data && data.message) {
                            this.message = data.message;
                        } else {
                            this.message = 'An error has occurred';
                        }
                    });

                this.close();


            } finally {
                this.is_confirming = false
            }
        },

        _teardown() {
            document.removeEventListener('keydown', this.onKeydown)
            this.selected_payload = null
            this.resend_content_type = null
            this.resend_amounts = {
                instant: 0,
                daily: 0,
                weekly: 0,
            }
            this.fetch_error = null
            this.is_fetching_text = false
            this.is_confirming = false
        },

        async fetch_text() {
            if ( this.fetch_url ) {
                try {

                    this.is_fetching_text = true;

                    fetch( `${this.fetch_url}?content_type_id=${this.content_type_id}&content_id=${this.content_id}`,
                        { credentials: 'same-origin' } )

                        .then(res => res.json())
                        .then(data => {
                            if (data && data.content_type && data.amounts) {
                                this.resend_content_type = data.content_type;
                                this.resend_amounts = {
                                    instant: Number(data.amounts.instant || 0),
                                    daily: Number(data.amounts.daily || 0),
                                    weekly: Number(data.amounts.weekly || 0),
                                };
                            } else {
                                this.fetch_error = 'No text received';
                            }
                            this.is_fetching_text = false;
                        });

                    console.log('Got the text');
                } catch (e) {
                    console.log(e);
                }
            }
        },
    },
});
</script>

<style scoped>
.loader {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 9999px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    display: inline-block;
    animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
